import { httpClient } from '@/modules/shared/services/http-client/http-client';

const ACTIVE_OFFICE_KEY = 'tt_active_company';

// Deduplicate overlapping TT bootstrap callers (guard, DefaultContainer, Shell).
let bootstrapPromise = null;
let officesPromise = null;
let mePromise = null;
let officesLoaded = false;
let meLoaded = false;
let bootstrapped = false;

function readStoredOffice() {
  const raw = localStorage.getItem(ACTIVE_OFFICE_KEY);
  const id = raw ? Number(raw) : NaN;
  return Number.isFinite(id) ? id : null;
}

function permissionObjectsFromSlugs(slugs) {
  return Array.from(new Set(slugs || [])).map((slug) => ({ slug, section: 'time_tracker' }));
}

function officePermissionSlugs(state) {
  const active = state.offices.find((o) => o.company_id === state.activeCompanyId);
  if (active && Array.isArray(active.permission_slugs)) {
    return active.permission_slugs;
  }
  return state.offices.flatMap((office) => office.permission_slugs || []);
}

function currentUser(rootGetters) {
  return rootGetters.getUserDetails || {};
}

function activeOffice(state) {
  return state.offices.find((o) => o.company_id === state.activeCompanyId) || null;
}

function officeRoleSlugs(state) {
  return state.offices.map((office) => office.time_tracker_role_slug).filter(Boolean);
}

export default {
  namespaced: true,

  state() {
    return {
      offices: [],
      activeCompanyId: readStoredOffice(),
      me: null,
      impersonator: null,
    };
  },

  getters: {
    isAuthenticated(_state, _getters, _rootState, rootGetters) {
      return rootGetters.getAuthStatus;
    },
    currentUser(_state, _getters, _rootState, rootGetters) {
      return currentUser(rootGetters);
    },
    offices: (state) => state.offices,
    activeCompanyId: (state) => state.activeCompanyId,
    activeOffice: (state) => activeOffice(state),
    permissions: (state) => permissionObjectsFromSlugs(officePermissionSlugs(state)),
    isSuperUser: (_state, _getters, _rootState, rootGetters) =>
      !!currentUser(rootGetters).super_user,
    isAdmin: (_state, _getters, _rootState, rootGetters) =>
      !!currentUser(rootGetters).admin,
    isOwner: (state, _getters, _rootState, rootGetters) => {
      const user = currentUser(rootGetters);
      const active = activeOffice(state);
      return !!(user.super_user || (active && active.time_tracker_role_slug === 'owner'));
    },
    hasPermission: (_state, _getters, _rootState, rootGetters) => (slug) => {
      const user = currentUser(rootGetters);
      if (user.super_user) return true;
      return officePermissionSlugs(_state).includes(slug);
    },
    hasAnyAdminAccess: (state, _getters, _rootState, rootGetters) => {
      const user = currentUser(rootGetters);
      if (!user || !Object.keys(user).length) return false;
      if (user.super_user) return true;
      if (officePermissionSlugs(state).length) return true;
      return officeRoleSlugs(state).some((slug) => ['owner', 'manager'].includes(slug));
    },
    canCreateManagers: (_state, getters, _rootState, rootGetters) => {
      const user = currentUser(rootGetters);
      if (user.super_user) return true;
      return getters.hasPermission('create_managers');
    },
    isEmployee: (state) => !!(state.me && state.me.is_employee),
    selfEmployee: (state) => (state.me ? state.me.employee : null),
    portalPeriods: (state) => (state.me ? state.me.periods || [] : []),
    isImpersonating: (state) => !!state.impersonator,
    impersonatorName: (state) =>
      state.impersonator ? state.impersonator.name : null,
  },

  mutations: {
    SET_OFFICES(state, offices) {
      state.offices = offices || [];
    },
    SET_ACTIVE_COMPANY(state, companyId) {
      state.activeCompanyId = companyId;
      if (companyId == null) {
        localStorage.removeItem(ACTIVE_OFFICE_KEY);
      } else {
        localStorage.setItem(ACTIVE_OFFICE_KEY, String(companyId));
      }
    },
    SET_ME(state, me) {
      state.me = me;
    },
    SET_IMPERSONATOR(state, payload) {
      state.impersonator = payload;
    },
    RESET_STATE(state) {
      state.offices = [];
      state.activeCompanyId = null;
      state.me = null;
      state.impersonator = null;
      localStorage.removeItem(ACTIVE_OFFICE_KEY);
      bootstrapPromise = null;
      officesPromise = null;
      mePromise = null;
      officesLoaded = false;
      meLoaded = false;
      bootstrapped = false;
    },
  },

  actions: {
    async bootstrap({ dispatch, rootGetters }) {
      if (!rootGetters.getAuthStatus) return;
      if (bootstrapped) return;
      if (bootstrapPromise) return bootstrapPromise;

      bootstrapPromise = (async () => {
        try {
          await dispatch('loadOffices');
          await dispatch('loadMe');
          bootstrapped = true;
        } catch (e) {
          if (e && e.response && e.response.status === 401) {
            dispatch('reset', null, { root: true });
          }
        } finally {
          bootstrapPromise = null;
        }
      })();

      return bootstrapPromise;
    },

    async loadOffices({ commit, state }, payload = {}) {
      const force = !!(payload && payload.force);
      if (!force && officesLoaded) return;
      if (!force && officesPromise) return officesPromise;

      officesPromise = (async () => {
        try {
          const { data } = await httpClient.get('/time-tracker/offices');
          if (data.success) {
            const offices = data.data.offices || [];
            commit('SET_OFFICES', offices);

            const stored = readStoredOffice();
            const preferred = [stored, state.activeCompanyId].find(
              (id) => id != null && offices.some((o) => o.company_id === id)
            );
            commit(
              'SET_ACTIVE_COMPANY',
              preferred ?? (offices.length ? offices[0].company_id : null)
            );
          }
          officesLoaded = true;
          return data;
        } finally {
          officesPromise = null;
        }
      })();

      return officesPromise;
    },

    async loadMe({ commit, state }, payload = {}) {
      const force = !!(payload && payload.force);
      if (!force && meLoaded) return state.me;
      if (!force && mePromise) return mePromise;

      mePromise = (async () => {
        try {
          const { data } = await httpClient.get('/time-tracker/me');
          if (data.success) {
            commit('SET_ME', data.data);
          }
          meLoaded = true;
        } catch (e) {
          commit('SET_ME', null);
          meLoaded = true;
        } finally {
          mePromise = null;
        }
        return state.me;
      })();

      return mePromise;
    },

    async switchOffice({ commit }, companyId) {
      const { data } = await httpClient.post('/time-tracker/office/switch', {
        company_id: companyId,
      });
      if (!data.success) {
        throw new Error(data.message || 'Could not switch office.');
      }
      commit('SET_ACTIVE_COMPANY', companyId);
      return data;
    },

    async markWalkthroughDone({ dispatch }) {
      try {
        const { data } = await httpClient.post('/time-tracker/user/complete-walkthrough');
        if (data.success) {
          await dispatch('pingAuth', { force: true }, { root: true });
        }
      } catch (e) {
        // cosmetic only
      }
    },

    reset({ commit }) {
      commit('RESET_STATE');
    },
  },
};
